import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AnalyticsEventsService } from '@techspert-io/analytics';
import { CognitoAuthService } from '@techspert-io/auth';
import { DataCaptureService } from '@techspert-io/data-capture';
import { OpportunityNavigationService } from '@techspert-io/navigation';
import { IOpportunity, IOpportunitySegment } from '@techspert-io/opportunities';
import { EditProjectKeywordsDialogComponent } from '@techspert-io/project-keywords';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  combineLatest,
} from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { UserPermission } from '../../features/auth/models/permissions';
import { InterviewPeriodStoreService } from '../../features/interview-period/services/interview-period-store.service';
import { OmnisearchService } from '../../features/omnisearch/services/omnisearch.service';
import {
  ExpertStoreService,
  IExpertPhases,
} from '../../shared/state/expert.service';
import { OpportunityStoreService } from '../../shared/state/opportunity.service';
import { RequestRecruitmentDialogComponent } from './components/request-recruitment-dialog/request-recruitment-dialog.component';

@Component({
  selector: 'app-opportunity',
  templateUrl: './opportunity.component.html',
  styleUrls: ['./opportunity.component.scss'],
  standalone: false,
})
export class OpportunityComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject();
  private tableRendered$ = new Subject();

  newExpertCount: number;
  showDescription = true;
  searchTerm: string;
  selectedIndex = 0;
  showOpp = true;

  vmExperts$: Observable<{
    experts: IExpertPhases;
    expertApprovalRequired: boolean;
    loading: boolean;
  }> = EMPTY;

  expertLoadingIds$ = this.expertStore.expertLoadingIds$;

  opportunity$: Observable<IOpportunity> = EMPTY;
  isExpertApprovalRequired$: Observable<{ value: boolean }> = EMPTY;
  showDataCapture: boolean;
  showDataCaptureSheetNotification$: Observable<{ showNotification: boolean }> =
    EMPTY;
  opportunitySetUp$ = new BehaviorSubject<boolean>(false);

  isProgrammaticChange = false;

  dataCaptureTermsAndConditions = new FormControl<boolean>(
    false,
    Validators.requiredTrue
  );

  submittedRecruitmentRequest = false;

  get canViewRejectedExperts() {
    return this.cognitoAuthService.loggedInUser?.permissions.has(
      UserPermission.ExpertsReadRejected
    );
  }

  get canAccessDataCapture() {
    return this.cognitoAuthService.loggedInUser?.permissions.has(
      UserPermission.DataCaptureRead
    );
  }

  private dashboardTab = 'dashboard';
  private tabs = [
    this.dashboardTab,
    'newExperts',
    'acceptedExperts',
    'completedConnections',
    'declinedExperts',
    'dataCapture',
  ];

  @ViewChild('tab-group') set table(_: HTMLElement) {
    this.tableRendered$.next(true);
  }

  constructor(
    private opportunityStore: OpportunityStoreService,
    private expertStore: ExpertStoreService,
    private activatedRoute: ActivatedRoute,
    private cognitoAuthService: CognitoAuthService,
    private router: Router,
    private titleService: Title,
    private omnisearchService: OmnisearchService,
    private opportunityNavigationState: OpportunityNavigationService,
    private dataCaptureService: DataCaptureService,
    private analyticsEventsService: AnalyticsEventsService,
    private toastService: ToastrService,
    private matDialog: MatDialog,
    private interviewPeriodStore: InterviewPeriodStoreService
  ) {}

  ngOnInit(): void {
    this.omnisearchService.omnisearchFocused$
      .pipe(tap((focused) => (this.showOpp = !focused)))
      .subscribe();

    const opp$ = this.activatedRoute.paramMap.pipe(
      map((params) => params.get('id')),
      distinctUntilChanged(),
      tap(() => this.opportunitySetUp$.next(false)),
      tap(() => this.toastService.clear()),
      tap(() => this.omnisearchService.setFocused(false)),
      switchMap((oppId) => this.opportunityStore.getOpportunity(oppId)),
      catchError((error) => {
        switch (error?.status) {
          case 401:
          case 403:
            this.router.navigate(['/not-authorized']);
            break;
          case 404:
          default:
            this.router.navigate(['not-found']);
        }

        return EMPTY;
      }),
      shareReplay(1)
    );

    this.opportunity$ = opp$.pipe(
      tap((opportunity) => this.setupOpportunity(opportunity))
    );

    this.isExpertApprovalRequired$ = opp$.pipe(
      switchMap((opp) =>
        this.cognitoAuthService.isClientFeatureEnabled(
          'expertApprovalRequired',
          opp.clientId
        )
      ),
      map((value) => ({ value }))
    );

    this.vmExperts$ = combineLatest([
      this.expertStore.experts$,
      this.isExpertApprovalRequired$,
      this.expertStore.loadingOppExperts$,
    ]).pipe(
      map(([experts, expertApprovalRequired, loading]) => ({
        experts,
        expertApprovalRequired: expertApprovalRequired.value,
        loading,
      }))
    );

    this.opportunityNavigationState.state$.subscribe();

    // Kept loaded for the page, so the experts' availability shows it at once
    opp$
      .pipe(
        switchMap(({ opportunityId }) =>
          this.interviewPeriodStore.getInterviewPeriod(opportunityId)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe();

    const hydrateSheets$ = opp$.pipe(
      switchMap((opp) =>
        this.dataCaptureService
          .querySheetsByOpportunityId(opp.opportunityId)
          .pipe(map(() => opp))
      )
    );

    this.showDataCaptureSheetNotification$ = combineLatest([
      this.dataCaptureService.allSheets$,
      hydrateSheets$,
      this.expertStore.experts$,
      this.opportunitySetUp$.pipe(filter(Boolean)),
    ]).pipe(
      map(([sheetMap, opp, experts]) => ({
        showNotification:
          !(sheetMap[opp.opportunityId] || []).length &&
          !['Self-Serve', 'RFQ'].includes(opp.stageName) &&
          this.showDataCapture &&
          Object.values(experts).flat().length > 0,
      }))
    );

    combineLatest([
      this.activatedRoute.queryParams,
      this.expertStore.experts$,
      this.opportunitySetUp$.pipe(filter(Boolean)),
    ])
      .pipe(
        tap(([queryParams, experts]) => this.setupRouting(queryParams, experts))
      )
      .subscribe();

    combineLatest([this.activatedRoute.queryParams, this.tableRendered$])
      .pipe(filter(([params]) => params.expertId))
      .subscribe(([params]) =>
        document.getElementById(params.expertId)?.scrollIntoView()
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
  }

  selectedTabChange(tabId: number): void {
    const mappedTab = this.mapTabId(tabId);

    const queryParams = mappedTab
      ? {
          ...(mappedTab !== this.dashboardTab && { tab: mappedTab }),
        }
      : {};

    this.router.navigate([], {
      queryParams,
      ...(this.isProgrammaticChange && { queryParamsHandling: 'merge' }),
    });

    this.isProgrammaticChange = false;
  }

  adjustTabIndex(tabId: number): void {
    this.selectedIndex = tabId;
  }

  navigateToSettings(opportunity: IOpportunity): void {
    this.router.navigate(['/connect', opportunity.publicDisplayId, 'settings']);
  }

  toggleShowDescription(): void {
    this.showDescription = !this.showDescription;
  }

  openOmnisearchFromProjectProgressPage(): void {
    this.omnisearchService.setFocused(true);

    if (this.cognitoAuthService.loggedInUser) {
      this.analyticsEventsService.sendEvent('click', {
        event_category: 'new_project_omnisearch_notification',
      });
    }
  }

  navigateScreener(opportunityId: string): void {
    window.open(`${environment.screenerEndpoint}/${opportunityId}`, '_blank');

    this.analyticsEventsService.sendEvent('click', {
      event_category: 'screener_comparison_navigation',
      opportunityId,
    });
  }

  acceptDataCaptureTermsAndConditions(
    clientId: string,
    opportunityId: string
  ): void {
    if (!this.dataCaptureTermsAndConditions.valid) {
      this.dataCaptureTermsAndConditions.markAsTouched();
      return;
    }

    this.opportunityStore
      .acceptOpportunityDataCaptureTermsAndConditions(clientId, opportunityId)
      .subscribe();
  }

  requestRecruitment(opportunity: IOpportunity) {
    if (opportunity.stageName === 'RFQ' || this.submittedRecruitmentRequest) {
      return this.toastService.warning(
        `We'll reach out with next steps at our earliest availability`,
        `You have already submitted a recruitment request for this project`
      );
    }

    interface IRecruitmentDialogData {
      opportunityId: string;
      segments: IOpportunitySegment[];
    }

    this.matDialog
      .open<RequestRecruitmentDialogComponent, IRecruitmentDialogData, boolean>(
        RequestRecruitmentDialogComponent,
        {
          width: '900px',
          height: '700px',
          data: {
            opportunityId: opportunity.opportunityId,
            segments: opportunity.opportunitySegments,
          },
          disableClose: true,
        }
      )
      .afterClosed()
      .subscribe((result) => {
        this.submittedRecruitmentRequest = result;
      });
  }

  manageKeywords(opportunityId: string) {
    this.matDialog
      .open(EditProjectKeywordsDialogComponent, {
        width: '600px',
        autoFocus: false,
        data: { opportunityId },
      })
      .afterClosed()
      .subscribe();
  }

  private setupOpportunity(opportunity: IOpportunity): void {
    this.omnisearchService.setViewedProject(opportunity.opportunityName);

    this.titleService.setTitle(opportunity.opportunityName);

    this.showDataCapture =
      this.canAccessDataCapture &&
      (opportunity.dataCapture?.enableCapture ||
        opportunity.dataCapture?.termsAndConditionsRequired);

    this.expertStore.setOpportunityId(opportunity.opportunityId);

    this.tabs = ['Self-Serve', 'RFQ'].includes(opportunity.stageName)
      ? ['dataCapture']
      : [
          this.dashboardTab,
          'newExperts',
          'acceptedExperts',
          'completedConnections',
          'declinedExperts',
          'dataCapture',
        ];

    this.opportunitySetUp$.next(true);
  }

  private setupRouting(params: Params, experts?: IExpertPhases): void {
    if (params.expertId && !params.tab) {
      const expertTab = Object.entries(experts || {})
        .map(([k, v]) => v.find((e) => e.expertId === params.expertId) && k)
        .find(Boolean);

      const tabIndex = this.mapTabName(expertTab);

      if (tabIndex) {
        this.opportunityNavigationState.setNavigationState({
          [params.expertId]: true,
        });

        if (this.selectedIndex !== tabIndex) {
          this.isProgrammaticChange = true;
          this.adjustTabIndex(tabIndex);
        }
      }
    } else if (params.tab) {
      const tabIndex = this.mapTabName(params.tab);

      if (params.tab === 'dataCapture' && !this.showDataCapture) {
        this.adjustTabIndex(0);
        return;
      }

      if (this.selectedIndex !== tabIndex) {
        this.isProgrammaticChange = true;
        this.adjustTabIndex(tabIndex);
      }
    }
  }

  private mapTabName(tabName: string) {
    const tabId = this.tabs.findIndex((tab) => tab === tabName);

    return tabId >= 0 ? tabId : 0;
  }

  private mapTabId(tabId: number) {
    return Object.fromEntries(Object.entries(this.tabs))[tabId] || undefined;
  }
}
